/**
 * Google Analytics 4 with strict consent gating (Ley 21.719 art. 12).
 *
 * Architecture:
 *  • gtag.js is NEVER injected on initial page load. We only fetch the script
 *    after the user explicitly grants analytics consent through the cookie
 *    banner (or has done so previously and the consent payload still matches).
 *  • Events queued before consent is granted are dropped on the floor — that's
 *    the legally-correct behavior. We do NOT buffer pre-consent traffic.
 *  • Identify-style calls strip PII (RUTs, emails). We only send categorical
 *    funnel labels and counts.
 */

const GA_ID = "G-E1E74Z65YD";
const CONSENT_KEY = "borrame_cookie_consent";
const CONSENT_VERSION = 1;
let initialized = false;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function hasAnalyticsConsent() {
  const c = readConsent();
  return !!(c && c.analytics);
}

function injectGtag() {
  if (initialized) return;
  if (typeof window === "undefined") return;

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // anonymize_ip retained for privacy-by-design (Ley 21.719 art. 9).
  gtag("config", GA_ID, { anonymize_ip: true, send_page_view: true });
  initialized = true;
  // eslint-disable-next-line no-console
  console.info("[analytics] gtag initialized for", GA_ID);
}

/**
 * Send a custom event. Silently no-ops when consent isn't granted.
 * Always strips obvious PII fields before forwarding.
 */
export function track(name, params = {}) {
  if (!hasAnalyticsConsent()) return;
  if (!initialized) injectGtag();
  const safe = stripPII(params);
  try {
    window.gtag && window.gtag("event", name, safe);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[analytics] track failed", name, e);
  }
}

/** Helper called on every React Router pathname change. */
export function trackPageView(path) {
  if (!hasAnalyticsConsent()) return;
  if (!initialized) injectGtag();
  try {
    window.gtag && window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  } catch {
    // ignore
  }
}

function stripPII(params) {
  const out = {};
  for (const [k, v] of Object.entries(params || {})) {
    if (typeof v === "string") {
      // Mask any value that looks like a Chilean RUT or an email.
      const looksRut = /^\d{1,2}\.?\d{3}\.?\d{3}-?[\dkK]$/.test(v.trim());
      const looksEmail = /@/.test(v);
      out[k] = (looksRut || looksEmail) ? "[redacted]" : v.slice(0, 100);
    } else {
      out[k] = v;
    }
  }
  return out;
}

/**
 * Boot analytics: if the visitor already opted in (returning user), inject
 * gtag immediately. Otherwise wait for the CookieBanner to dispatch the
 * "borrame:analytics-opt-in" event.
 *
 * Safe to call once from App on mount.
 */
export function bootAnalytics() {
  if (typeof window === "undefined") return;
  if (hasAnalyticsConsent()) {
    injectGtag();
  }
  window.addEventListener("borrame:analytics-opt-in", () => {
    injectGtag();
    track("consent_granted", { source: "cookie_banner" });
  });
}
